<template>
  <div class="site-card">
    <div class="site-header">
      <div class="site-info">
        <h3 class="site-name">{{ site.name }}</h3>
        <p class="company-name">{{ $t('company') }}: {{ site.company_name }}</p>
        <div class="site-stats">
          <div class="stat-item" v-if="site.rooms_with_open_complaints_percentage !== undefined">
            <span class="stat-label">{{ $t('rooms_with_open_complaints_percentage') }}:</span>
            <span class="stat-value percentage">{{ site.rooms_with_open_complaints_percentage }}%</span>
          </div>
          <div class="stat-item" v-if="site.rooms_without_cleaner_arrival_percentage !== undefined">
            <span class="stat-label">{{ $t('rooms_without_cleaner_arrival_percentage') }}:</span>
            <span class="stat-value percentage warning">{{ site.rooms_without_cleaner_arrival_percentage }}%</span>
          </div>
        </div>
      </div>
      <div class="action-buttons">
        <button
          @click.prevent="$emit('whatsapp-click', site)"
          class="whatsapp-button"
          type="button"
          :title="$t('send_whatsapp')"
        >
          <svg
            class="whatsapp-icon"
            viewBox="0 0 24 24"
            fill="currentColor"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"
            />
          </svg>
          <span>{{ $t('whatsapp') }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SiteCard',
  props: {
    site: {
      type: Object,
      required: true,
    },
  },
}
</script>

<style lang="scss" scoped>
@use '@/styles/vars.scss' as *;

.site-card {
  background-color: $white;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  position: relative;
  overflow: visible;

  &:hover {
    box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.12);
    transform: translateY(-2px);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: linear-gradient(180deg, #25d366 0%, #128c7e 100%);
    z-index: 1;
  }
}

.site-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 20px;
}

.site-info {
  flex: 1;
  min-width: 0;
}

.site-name {
  font-size: 1.5rem;
  font-weight: $font-weight-bold;
  margin: 0 0 8px 0;
  color: $black-blue;
  word-wrap: break-word;
}

.company-name {
  font-size: 1rem;
  color: $dark-grey;
  margin: 0 0 16px 0;
  font-weight: $font-weight-semi-bold;
}

.site-stats {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
}

.stat-label {
  color: $dark-grey;
  font-weight: $font-weight-semi-bold;
}

.stat-value {
  font-weight: $font-weight-bold;
  
  &.duration {
    color: #f57c00;
  }
  
  &.percentage {
    &.success {
      color: #25d366;
    }
    
    &.warning {
      color: #ff9800;
    }
    
    &:not(.success):not(.warning) {
      color: #e91e63;
    }
  }
}

.action-buttons {
  display: flex;
  align-items: center;
  gap: 8px;
}

.whatsapp-button {
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: #25d366;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 20px;
  font-size: 0.95rem;
  font-weight: $font-weight-semi-bold;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  box-shadow: 0 2px 4px rgba(37, 211, 102, 0.3);

  &:hover:not(:disabled) {
    background-color: #20ba5a;
    box-shadow: 0 4px 8px rgba(37, 211, 102, 0.4);
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
}

.whatsapp-icon {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

@media (max-width: 768px) {
  .site-card {
    padding: 20px;
  }

  .site-header {
    flex-direction: column;
    gap: 16px;
  }

  .whatsapp-button {
    width: 100%;
    justify-content: center;
  }

  .site-stats {
    flex-direction: row;
    flex-wrap: wrap;
  }

  .action-buttons {
    width: 100%;
  }

  .whatsapp-button {
    width: 100%;
    justify-content: center;
  }
}
</style>

